import {
  put,
  call,
  takeEvery,
  select,
  fork,
  cancelled,
  take,
  delay,
} from 'redux-saga/effects';
import { eventChannel } from 'redux-saga';
import ActionTypes from '../constants/ActionTypes';
import {
  podBasedGetEvaluationDetails,
  podBasedRunCode,
  getEvaluationLog,
  getTestProjectDisabledSlugsApi,
} from '../api/podBased';

import { errorLogger } from '../utils/trackingUtil';
import {
  POD_EVAL_WEBSOCKET_EVENTS,
  WEBSOCKET_CLOSE_REASONS,
} from '../components/ProblemTypes/PodBasedSolution/constants';
import { EVALUATION_STATE } from '../constants/Constants';
import {
  getFinalTestCasesForPodBased,
  getTestCasesFromJuit,
} from '../utils/helperFunction';

function* getPodBasedEvaluationDetails(args) {
  try {
    if (args.solutionSlug) {
      const response = yield call(
        podBasedGetEvaluationDetails,
        args.solutionSlug
      );

      yield put({
        type: ActionTypes.GET_POD_BASED_EVALUATION_DETAILS,
        payload: {
          evaluationEngineTaskId: response.data.evaluation_engine_task_id,
          runDetails: response.data.run_details,
        },
      });
    }
  } catch (err) {
    errorLogger(err);
  }
}

function* getPodBasedCurrentEvaluationId(args) {
  try {
    if (args.solutionSlug) {
      const response = yield call(
        podBasedGetEvaluationDetails,
        args.solutionSlug
      );
      yield put({
        type: ActionTypes.GET_POD_BASED_CURRENT_EVALUATION_ID,
        payload: response.data?.evaluation_engine_task_id,
      });
    }
  } catch (err) {
    errorLogger(err);
  }
}

function* runPodBasedTestProject(args) {
  try {
    if (args.solutionSlug) {
      const response = yield call(podBasedRunCode, args.solutionSlug);
      yield put({
        type: ActionTypes.RUN_POD_BASED_TEST_PROJECT,
        payload: response.data.s3_http_log_url,
      });
    }
  } catch (err) {
    errorLogger(err);
  }
}

function* getTestProjectDisabledSlugs() {
  try {
    const response = yield call(getTestProjectDisabledSlugsApi);
    yield put({
      type: `${ActionTypes.GET_TEST_PROJECT_DISABLED_SLUGS}_SUCCESS`,
      payload: response.data?.disabled_slugs,
    });
  } catch (err) {
    errorLogger(err);
  }
}

function* getPodBasedEvaluationLog(args) {
  try {
    if (args.logUrl) {
      const logResponse = yield call(getEvaluationLog, args.logUrl);
      if (logResponse.status !== 200) {
        yield put({
          type: `${ActionTypes.SET_POD_BASED_EVALUATION_STATE}_LOADING`,
        });
        return;
      }

      const textContent = yield call(resp => resp.text(), logResponse);
      yield put({
        type: ActionTypes.GET_POD_BASED_EVALUATION_LOG,
        payload: {
          logContent: textContent,
          logContentLength: textContent.length,
        },
      });
    }
  } catch (err) {
    if (err.response && err.response.status !== 404) {
      errorLogger(err);
    }
  }
}

function createWebSocketChannel(podWebsocketUrl) {
  return eventChannel(emitter => {
    const evalSocket = new WebSocket(podWebsocketUrl);

    evalSocket.onopen = () => {
      emitter({ type: 'open', payload: evalSocket });
    };

    evalSocket.onmessage = event => {
      emitter({
        type: 'message',
        payload: event.data,
      });
    };

    evalSocket.onerror = error => {
      emitter({ type: 'error', payload: error });
    };

    evalSocket.onclose = event => {
      emitter({ type: 'close', payload: event });
    };

    return () => {
      evalSocket.close();
    };
  });
}

function* handleWebSocketMessages(
  channel,
  problemSlug,
  scrollCallback,
  podWebsocketUrl,
  initialTestCases
) {
  try {
    while (true) {
      const { type, payload } = yield take(channel);
      const testProjectStates = yield select(
        state => state.podBased.testProjectStates
      );

      const evalSocket =
        type === 'open' && payload
          ? payload
          : testProjectStates[problemSlug].socketId;

      switch (type) {
        case 'open':
          try {
            testProjectStates[problemSlug].socketId = payload;
            testProjectStates[problemSlug].websocketLastEvent = '';
            testProjectStates[problemSlug].connectionRetries = 0;
            testProjectStates[problemSlug].retriesAttempts =
              testProjectStates[problemSlug].retriesAttempts > 0
                ? testProjectStates[problemSlug].retriesAttempts
                : 0;
            testProjectStates[problemSlug].logMessage = '';
            testProjectStates[problemSlug].evaluationState =
              EVALUATION_STATE.LOADING;
            evalSocket.send(POD_EVAL_WEBSOCKET_EVENTS.MALPRACTICE_CHK);
            yield put({
              type: `${ActionTypes.SET_POD_BASED_TESTPROJECTSTATES}_UPDATE`,
              payload: {
                ...testProjectStates,
              },
            });
          } catch (e) {
            errorLogger(e);
          }
          break;

        case 'message':
          if (
            testProjectStates?.[problemSlug]?.websocketLastEvent ===
            POD_EVAL_WEBSOCKET_EVENTS.EVALUATE
          ) {
            testProjectStates[
              problemSlug
            ].logMessage = `${testProjectStates[problemSlug].logMessage}\n${payload}`;
            scrollCallback();
          }
          if (payload === POD_EVAL_WEBSOCKET_EVENTS.NO_MALPRACTICE) {
            evalSocket.send(POD_EVAL_WEBSOCKET_EVENTS.EVALUATE);
            testProjectStates[problemSlug].evaluationState =
              EVALUATION_STATE.EVALUATING;
            testProjectStates[problemSlug].websocketLastEvent =
              POD_EVAL_WEBSOCKET_EVENTS.EVALUATE;
            testProjectStates[problemSlug].logMessage = '';
          }
          if (
            payload.includes(POD_EVAL_WEBSOCKET_EVENTS.MALPRACTICE_DETECTED)
          ) {
            testProjectStates[problemSlug].evaluationState =
              EVALUATION_STATE.MAL_PRACTICE_ERROR;
            testProjectStates[problemSlug].logMessage = '';
            evalSocket.close();
            testProjectStates[problemSlug].socketId = undefined;
          }
          if (
            testProjectStates[problemSlug]?.websocketLastEvent ===
            POD_EVAL_WEBSOCKET_EVENTS.XML
          ) {
            try {
              const testFiles = JSON.parse(payload);
              let testCases = [];
              Object.keys(testFiles).forEach(key => {
                const framework =
                  testProjectStates[problemSlug]?.xmlFiles?.[key];
                const testcases = getTestCasesFromJuit(testFiles[key], {
                  framework,
                });
                testCases = [...testCases, ...testcases];
              });
              const finalTestCases = getFinalTestCasesForPodBased(
                initialTestCases,
                testCases
              );
              testProjectStates[problemSlug].testcases = finalTestCases;
            } catch (error) {
              errorLogger(error);
              showErrorToast('Issue in evaluation, try again !!');
            }
            testProjectStates[problemSlug].websocketLastEvent = '';
            testProjectStates[problemSlug].evaluationState =
              EVALUATION_STATE.EVALUATED;
            evalSocket.close();
            testProjectStates[problemSlug].socketId = undefined;
          }
          if (
            testProjectStates[problemSlug]?.websocketLastEvent ===
            POD_EVAL_WEBSOCKET_EVENTS.CONFIGURATION
          ) {
            const config = JSON.parse(payload);
            testProjectStates[problemSlug].xmlFiles = config?.scoring?.files;
            evalSocket.send(POD_EVAL_WEBSOCKET_EVENTS.XML);
            testProjectStates[problemSlug].websocketLastEvent =
              POD_EVAL_WEBSOCKET_EVENTS.XML;
          }
          if (
            payload?.includes('EOF') &&
            testProjectStates[problemSlug]?.websocketLastEvent ===
              POD_EVAL_WEBSOCKET_EVENTS.EVALUATE
          ) {
            evalSocket.send(POD_EVAL_WEBSOCKET_EVENTS.CONFIGURATION);
            testProjectStates[problemSlug].websocketLastEvent =
              POD_EVAL_WEBSOCKET_EVENTS.CONFIGURATION;
          }
          yield put({
            type: `${ActionTypes.SET_POD_BASED_TESTPROJECTSTATES}_UPDATE`,
            payload: {
              ...testProjectStates,
            },
          });
          break;

        case 'error':
          console.error('Pod based webSocket error:', payload);
          errorLogger(payload);
          break;

        case 'close':
          console.info('Pod based webSocket closed');
          if (WEBSOCKET_CLOSE_REASONS.includes(payload.reason)) {
            errorLogger({
              message: payload.reason,
              name: payload.wasClean
                ? 'WebSocket connection closed cleanly'
                : 'WebSocket connection closed unexpectedly',
              errorSource: podWebsocketUrl,
            });
            if (
              testProjectStates[problemSlug].connectionRetries < 1 &&
              testProjectStates[problemSlug].retriesAttempts < 3
            ) {
              yield delay(30 * 1000);
              testProjectStates[problemSlug].connectionRetries =
                (testProjectStates[problemSlug].connectionRetries ?? 0) + 1;
              testProjectStates[problemSlug].retriesAttempts =
                (testProjectStates[problemSlug].retriesAttempts ?? 0) + 1;
              put({
                type: `${ActionTypes.SET_POD_BASED_TESTPROJECTSTATES}_UPDATE`,
                payload: {
                  ...testProjectStates,
                },
              });
              yield put({
                type: ActionTypes.INITIATE_POD_BASED_EVALUATION,
                payload: {
                  problemSlug,
                  podWebsocketUrl,
                },
              });
            }
          }
          break;

        default:
          break;
      }
    }
  } finally {
    if (yield cancelled()) {
      channel.close();
    }
  }
}

function* initiatePodBasedEvaluation(args) {
  try {
    const {
      problemSlug,
      podWebsocketUrl,
      scrollCallback = () => {},
      initialTestCases,
    } = args.payload;
    const testProjectStates = yield select(
      state => state.podBased.testProjectStates
    );

    // Check if a WebSocket connection already exists for this problemSlug
    if (testProjectStates[problemSlug]?.socketId) {
      console.warn(`WebSocket already exists for problemSlug: ${problemSlug}`);
      return;
    }
    if (!testProjectStates[problemSlug]) testProjectStates[problemSlug] = {};

    const socketChannel = yield call(
      createWebSocketChannel,
      podWebsocketUrl,
      problemSlug
    );

    yield fork(
      handleWebSocketMessages,
      socketChannel,
      problemSlug,
      scrollCallback,
      podWebsocketUrl,
      initialTestCases
    );
  } catch (e) {
    errorLogger(e);
  }
}

export default function* podBasedSaga() {
  yield takeEvery(
    ActionTypes.GET_POD_BASED_EVALUATION_DETAILS,
    getPodBasedEvaluationDetails
  );
  yield takeEvery(
    ActionTypes.GET_POD_BASED_CURRENT_EVALUATION_ID,
    getPodBasedCurrentEvaluationId
  );
  yield takeEvery(
    ActionTypes.RUN_POD_BASED_TEST_PROJECT,
    runPodBasedTestProject
  );
  yield takeEvery(
    ActionTypes.GET_POD_BASED_EVALUATION_LOG,
    getPodBasedEvaluationLog
  );
  yield takeEvery(
    ActionTypes.INITIATE_POD_BASED_EVALUATION,
    initiatePodBasedEvaluation
  );
  yield takeEvery(
    ActionTypes.GET_TEST_PROJECT_DISABLED_SLUGS,
    getTestProjectDisabledSlugs
  );
}
